import { ProfileListItem } from "./types";

/** Track whether profile accordion initialization has run */
let hasProfileInitRun = false;

/**
 * Initializes all profile accordion sections
 * Call this when the DOM is ready
 */
export function initializeProfileAccordions(): void {
    if (hasProfileInitRun) return;
    hasProfileInitRun = true;

    const profileSections: ProfileListItem[] = [
        {
            showMoreId: "btnCountriesShowMore",
            showLessId: "btnCountriesShowLess",
            containerId: "countryListContainer",
            batchClassPrefix: "country-batch",
        },
        {
            showMoreId: "btnLanguagesShowMore",
            showLessId: "btnLanguagesShowLess",
            containerId: "languageListContainer",
            batchClassPrefix: "language-batch",
        },
        {
            showMoreId: "btnIndustriesShowMore",
            showLessId: "btnIndustriesShowLess",
            containerId: "industryListContainer",
            batchClassPrefix: "industry-batch",
        },
        {
            showMoreId: "btnExpertisesShowMore",
            showLessId: "btnExpertisesShowLess",
            containerId: "expertiseListContainer",
            batchClassPrefix: "expertise-batch",
        },
        {
            showMoreId: "btnOfferingsShowMore",
            showLessId: "btnOfferingsShowLess",
            containerId: "offeringListContainer",
            batchClassPrefix: "offering-batch",
        },
        {
            showMoreId: "btnCustomerTypesShowMore",
            showLessId: "btnCustomerTypesShowLess",
            containerId: "customerTypeListContainer",
            batchClassPrefix: "customertype-batch",
        },
    ];

    profileSections.forEach((section) => {
        initProfileAccordionActions(section);
    });
}

/**
 * Initializes accordion actions for a specific profile section
 */
function initProfileAccordionActions(section: ProfileListItem): void {
    const container = document.getElementById(section.containerId);
    if (!container) return;

    const totalCountValue = SaNumberUtils.coerceToInteger(
        container.dataset.totalCount || "0",
    );

    // No need to initialize if there are 3 or fewer items
    if (Number.isNaN(totalCountValue) || totalCountValue <= 3) {
        return;
    }

    const btnMore = document.getElementById(section.showMoreId);
    const btnLess = document.getElementById(section.showLessId);

    if (!btnMore || !btnLess) return;

    // Calculate total batches (5 items per batch after the initial 3)
    const totalGroups = Math.ceil((totalCountValue - 3) / 5);

    btnMore.addEventListener("click", () => {
        revealCurrentProfileBatch(
            btnMore,
            btnLess,
            container,
            totalGroups,
            section.batchClassPrefix,
        );
    });

    btnLess.addEventListener("click", () => {
        collapseCurrentProfileBatch(btnMore, btnLess, container);
    });
}

/**
 * Reveals the next batch of profile items
 */
function revealCurrentProfileBatch(
    btnMore: HTMLElement,
    btnLess: HTMLElement,
    container: HTMLElement,
    totalGroups: number,
    batchClassPrefix: string,
): void {
    let currentGroup = SaNumberUtils.coerceToInteger(
        btnMore.dataset.nextGroup || "0",
    );

    // Get all elements for the current batch using the dynamic class prefix
    const elementsToExpand = container.querySelectorAll(
        `.${batchClassPrefix}-${currentGroup}`,
    );

    // Use Bootstrap's Collapse API to show each element
    elementsToExpand.forEach((el) => {
        const bsCollapse = window.bootstrap?.Collapse?.getOrCreateInstance(el, {
            toggle: false,
        });
        if (bsCollapse) {
            bsCollapse.show();
        }
    });

    currentGroup++;
    btnMore.dataset.nextGroup = currentGroup.toString();

    // Always show 'Show less' once we expand beyond the initial 3
    btnLess.classList.remove("d-none");

    // Hide 'Show more' if all batches are revealed
    if (currentGroup >= totalGroups) {
        btnMore.classList.add("d-none");
    }
}

/**
 * Collapses all profile items back to the initial 3
 */
function collapseCurrentProfileBatch(
    btnMore: HTMLElement,
    btnLess: HTMLElement,
    container: HTMLElement,
): void {
    // Hide all collapsed batches
    const allCollapsible = container.querySelectorAll("li.collapse");

    allCollapsible.forEach((el) => {
        const bsCollapse = window.bootstrap?.Collapse?.getOrCreateInstance(el, {
            toggle: false,
        });
        if (bsCollapse) {
            bsCollapse.hide();
        }
    });

    // Reset state back to original 3 items
    btnMore.dataset.nextGroup = "0";
    btnMore.classList.remove("d-none");
    btnLess.classList.add("d-none");
}

export function setupProfileAccordionOnReady(): void {
    initializeProfileAccordions();
}

// Export internal functions for testing purposes
export const __testExports = {
    revealCurrentProfileBatch,
    collapseCurrentProfileBatch,
};
